// Taken from serialize-json npm library
// @W-14338255@ Allow / since it's harmless with > escaped:
// https://gus.lightning.force.com/lightning/r/ADM_Work__c/a07EE00001cq6y3YAA/view

import { IS_DEVELOPMENT } from '~/modules/config';

// const REGEXP_UNSAFE_FOR_TEXT = /["<>/\u2028\u2029]/g; // also replaces '/' (disabled)
const REGEXP_UNSAFE_FOR_TEXT = /["<>\u2028\u2029]/g;

// Escape script tags and angle brackets to ensure we don't accidentally break the HTML parser state
// or terminate the script section in the middle of a JSON payload.
// https://www.man42.net/blog/2016/12/safely-escape-user-data-in-a-script-tag
// https://html.spec.whatwg.org/multipage/scripting.html#restrictions-for-contents-of-script-elements
// Escaping all < and > prevents sequences like <!--<script> from driving the parser into
// script-data-double-escaped state, which breaks page integrity.
const REGEXP_UNSAFE_FOR_JSON = /[<>]/g;
const REGEXP_UNSAFE_FOR_HTML = /[&"'<>]/g;

const HTML_ESCAPE_MAP: Record<string, string> = {
    '&': '&amp;',
    '"': '&quot;',
    "'": '&#39;',
    '<': '&lt;',
    '>': '&gt;',
};

// Mapping of unsafe strings to safe counterparts that can be serialized into HTML documents
const REPLACE_MAP: Record<string, string> = {
    '<': '\\u003C',
    '>': '\\u003E',
    '/': '\\u002F',
    '"': '\\u0022',
    '\u2028': '\\u2028',
    '\u2029': '\\u2029',
};

function replacer(text: string) {
    return REPLACE_MAP[text.toLowerCase()];
}

export function sanitizeText(text: string) {
    return text.replace(REGEXP_UNSAFE_FOR_TEXT, replacer);
}

/**
 * Escapes plain text for interpolation into HTML text or a quoted HTML attribute.
 *
 * JavaScript-style Unicode escapes such as `\u0022` are not decoded by the HTML parser and
 * therefore render as literal text. HTML entities preserve the original value after parsing while
 * keeping quotes and markup delimiters inside the attribute.
 */
export function escapeHtml(text: string) {
    return text.replace(REGEXP_UNSAFE_FOR_HTML, (character) => HTML_ESCAPE_MAP[character]);
}

export function sanitizeJsonText(text: string) {
    return text.replace(REGEXP_UNSAFE_FOR_JSON, replacer);
}

export function sanitizeJson(json: object) {
    // Convert the JSON object to a string, applying indentation if in development environment.
    // In lower environments like development, indentation helps with readability and debugging.
    return sanitizeJsonText(JSON.stringify(json, undefined, IS_DEVELOPMENT ? 2 : undefined));
}

// Max length for CMS-provided color / gradient strings bound to inline CSS custom properties.
const MAX_SANITIZE_COLOR_LENGTH = 8192;

// Allowed characters for CSS color values used in style attributes / custom properties.
// Excludes quotes, semicolons, colons, backslashes, angle brackets, and @ (blocks @import etc.).
const REGEXP_SAFE_CSS_COLOR_CHARS = /^[a-zA-Z0-9#().,%+\-_/ ]+$/;

// Substrings that must not appear in values interpolated into CSS (case-insensitive).
const REGEXP_DANGEROUS_CSS_COLOR = new RegExp(
    [
        String.raw`url\s*\(`,
        String.raw`expression\s*\(`,
        String.raw`javascript\s*:`,
        String.raw`@import`,
        String.raw`behavior\s*:`,
        String.raw`-moz-binding`,
        String.raw`data\s*:`,
        String.raw`vbscript\s*:`,
        String.raw`<\s*script`,
        String.raw`<\/`,
    ].join('|'),
    'i',
);

export function containsControlCharacter(s: string): boolean {
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        if (c <= 0x1f || (c >= 0x7f && c <= 0x9f)) {
            return true;
        }
    }
    return false;
}

// Sanitizes a string intended for use in a CSS color or background value (e.g. custom properties
// in a style attribute). Returns {@link fallback} when the input is missing, too long, contains
// control characters, disallowed punctuation, or known-dangerous CSS constructs (e.g. url(), expression()).
export function sanitizeColor(input: string | null | undefined, fallback: string): string {
    if (input == null || typeof input !== 'string') {
        return fallback;
    }
    const trimmed = input.trim();
    if (!trimmed.length || trimmed.length > MAX_SANITIZE_COLOR_LENGTH) {
        return fallback;
    }
    if (
        containsControlCharacter(trimmed) ||
        !REGEXP_SAFE_CSS_COLOR_CHARS.test(trimmed) ||
        REGEXP_DANGEROUS_CSS_COLOR.test(trimmed)
    ) {
        return fallback;
    }
    return trimmed;
}

export const INERT_SCRIPT_URL = 'data:text/javascript,';

export function sanitizeScriptUrl(url: string) {
    try {
        if (!url || typeof url !== 'string' || url.trim() === '') {
            console.warn('Blocked: empty or invalid input');
            return INERT_SCRIPT_URL;
        }

        const parsedUrl = new URL(url);

        if (parsedUrl.protocol !== 'https:') {
            console.warn(`Blocked script: invalid protocol (${parsedUrl.protocol})`);
            return INERT_SCRIPT_URL;
        }

        if (parsedUrl.hostname.endsWith('.salesforce.com') || parsedUrl.hostname === 'salesforce.com') {
            return parsedUrl.href;
        }

        console.warn(`Blocked script from unauthorized domain: ${parsedUrl.hostname}`);
        return INERT_SCRIPT_URL;
    } catch (e) {
        console.warn(`Invalid script URL: ${url}`);
        return INERT_SCRIPT_URL;
    }
}
